import { TOKEN_VARIANT_CONFIGS, SINGLE_VARIANT } from '../types';
import { deriveColorVariants, getBestTextColor, getDisabledTextColor, hexToHsl, hslToHex, deriveAlertSubtleColor } from './color';

// Colors
export function derivePrimaryColorVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  const textColor = getBestTextColor(baseColor);
  const disabledTextColor = getDisabledTextColor(baseColor);
  return {
    'fill-primary-default': variants.default,
    'fill-primary-hover': variants.hover,
    'fill-primary-pressed': variants.pressed,
    'fill-primary-disabled': variants.disabled,
    'border-primary-default': baseColor,
    'text-primary-default': textColor,
    'icon-primary-default': textColor,
    'text-primary-disabled': disabledTextColor
  };
}
export function deriveSecondaryColorVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  const disabledTextColor = getDisabledTextColor(baseColor);
  return {
    'fill-secondary-default': variants.default,
    // keeping this as full color so other teams can use this secondary color for other purposes besides buttons
    'fill-secondary-default-alt': 'transparent',
    'fill-secondary-hover': variants.hover,
    'fill-secondary-hover-alt': variants.subtle,
    'fill-secondary-pressed': variants.pressed,
    'fill-secondary-pressed-alt': variants.pressedSubtle,
    'fill-secondary-disabled': 'transparent',
    'fill-secondary-disabled-alt': variants.disabled,
    'border-secondary-default': baseColor,
    'border-secondary-default-alt': baseColor,
    'text-secondary-default': variants.pressed,
    'text-secondary-disabled': disabledTextColor
  };
}
export function deriveTertiaryColorVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  const textColor = getBestTextColor(baseColor);
  const disabledTextColor = getDisabledTextColor(baseColor);
  return {
    'fill-tertiary-default': variants.default,
    'fill-transitional-tertiary-default': variants.default,
    'fill-transitional-tertiary-hover': variants.hover,
    'fill-transitional-tertiary-pressed': variants.pressed,
    'fill-tertiary-hover': variants.hover,
    'fill-tertiary-pressed': variants.pressed,
    'fill-tertiary-disabled': variants.disabled,
    'border-tertiary-default': baseColor,
    'text-inverse-default': textColor,
    // The token below is used on tertiary-light versions of buttons
    'text-tertiary-default': textColor,
    'text-tertiary-disabled': disabledTextColor
  };
}
export function deriveSurfaceTokensFromStyle(style) {
  if (style === 'dark') {
    return deriveDarkSurfaceOverrides();
  }
  return deriveLightSurfaceOverrides();
}

// Dark mode: rely on the Trellis dark theme as the base. Only override tokens
// where portals intentionally diverges from Trellis dark.
//
// foundations-theming-base/stable/* paths are non-public and banned by lint,
// so these are hardcoded from the trellis-dark theme.
const TRELLIS_DARK_SURFACE_DEFAULT = '#1F1F1F';
const TRELLIS_DARK_SURFACE_RAISED = '#292929';
function deriveDarkSurfaceOverrides() {
  const surfaceHsl = hexToHsl(TRELLIS_DARK_SURFACE_DEFAULT);
  const surfaceVariants = deriveColorVariants(TRELLIS_DARK_SURFACE_DEFAULT);
  const BORDER_LIGHTNESS_CONFIG = {
    maxSaturation: 10,
    subtle: {
      offset: 8,
      darkMin: 20
    },
    default: {
      offset: 16,
      darkMin: 30
    },
    hover: {
      offset: 32,
      darkMin: 45
    }
  };
  const borderSubtle = hslToHex(Object.assign({}, surfaceHsl, {
    l: Math.max(Math.min(surfaceHsl.l + BORDER_LIGHTNESS_CONFIG.subtle.offset, 100), BORDER_LIGHTNESS_CONFIG.subtle.darkMin),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation)
  }));
  const borderDefault = hslToHex(Object.assign({}, surfaceHsl, {
    l: Math.max(Math.min(surfaceHsl.l + BORDER_LIGHTNESS_CONFIG.default.offset, 100), BORDER_LIGHTNESS_CONFIG.default.darkMin),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation)
  }));
  const borderHover = hslToHex(Object.assign({}, surfaceHsl, {
    l: Math.max(Math.min(surfaceHsl.l + BORDER_LIGHTNESS_CONFIG.hover.offset, 100), BORDER_LIGHTNESS_CONFIG.hover.darkMin),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation)
  }));
  const TABLE_HEADER_LIGHTNESS_OFFSET = 5;
  const tableHeaderDefault = hslToHex(Object.assign({}, surfaceHsl, {
    l: Math.min(surfaceHsl.l + TABLE_HEADER_LIGHTNESS_OFFSET, 100)
  }));
  const tableHeaderVariants = deriveColorVariants(tableHeaderDefault);
  return {
    // Border tokens derived from the dark surface base. Trellis dark leaves
    // border-transitional-core-subtle at a light value (#F5F5F5) which renders
    // as a bright outline on dark popovers; the others are overridden for
    // consistent contrast against the dark surface.
    'border-core-default': borderDefault,
    'border-core-subtle': borderSubtle,
    'border-core-disabled': '#4a4a4a',
    // darker than Trellis #333333 for better visibility on dark surfaces

    'border-core-hover': borderHover,
    'border-transitional-core-subtle': borderSubtle,
    // trellis-dark maps border-transitional-core-subtle-alt to #F5F5F5 (near-white), producing
    // a stark visible border on Modal/Dialog in dark mode. Use the same subtle value as other
    // dark surface borders so it blends with the dark background.
    'border-transitional-core-subtle-alt': borderSubtle,
    // Dark field backgrounds (trellis-dark gap, uses same white #FFFFFF values as trellis-light)
    'fill-field-default': TRELLIS_DARK_SURFACE_RAISED,
    'fill-field-default-alt': surfaceVariants.hover,
    'fill-field-disabled': '#2a2a2a',
    // darker than Trellis #333333 to distinguish disabled from default field
    'fill-field-hover': surfaceVariants.hover,
    // Dark-appropriate table headers (trellis-dark has light-themed values)
    'specialty-table-header-default': tableHeaderDefault,
    'specialty-table-header-hover': tableHeaderVariants.hover,
    'specialty-table-header-pressed': tableHeaderVariants.pressed,
    // trellis-dark defines fill-transparent-default as #FFFFFF (should be transparent)
    'fill-transparent-default': 'rgba(255,255,255,0)',
    // trellis-dark defines specialty-scrim as #141414 (fully opaque), unlike trellis-light
    // which uses rgba(20,20,20,0.46). Override so modal overlays remain semi-transparent.
    'specialty-scrim': 'rgba(20,20,20,0.5)',
    // trellis-dark maps fill-inverse-hover-transparent to #FFFFFF (solid white), unlike
    // trellis-light which uses rgba(20,20,20,0.08). This token controls the close button
    // hover background — override to a subtle white overlay so the icon remains visible.
    'fill-inverse-hover-transparent': 'rgba(255,255,255,0.1)',
    // used for PanelHeaders which expects two colors for a
    // gradient background-image. These colors will a subtle gradient
    // that I think is appropriate until we get more customer feedback.
    'fill-transitional-modal-gradient-color-1': surfaceVariants.default,
    'fill-transitional-modal-gradient-color-2': surfaceVariants.default,
    // deriveTertiaryColorVariants sets text-inverse-default to getBestTextColor(tertiaryColor)
    // so it contrasts with the light-mode modal gradient (brand colors). In dark mode the modal
    // gradient uses dark surface colors instead, so the title text must be light.
    // Trellis dark base value: #F5F5F5.
    'text-inverse-default': getBestTextColor(TRELLIS_DARK_SURFACE_DEFAULT)
  };
}

// Light mode: override only tokens where portals intentionally diverges from
// Trellis light (subtler borders, WCAG AA disabled text, gray fields, custom
// accent neutral). All other surface tokens come from the Trellis light base.
const TRELLIS_LIGHT_SURFACE_DEFAULT = '#FFFFFF';
function deriveLightSurfaceOverrides() {
  const surfaceHsl = hexToHsl(TRELLIS_LIGHT_SURFACE_DEFAULT);
  const surfaceVariants = deriveColorVariants(TRELLIS_LIGHT_SURFACE_DEFAULT);
  const BORDER_LIGHTNESS_CONFIG = {
    maxSaturation: 10,
    subtle: {
      offset: 8
    },
    default: {
      offset: 16
    },
    hover: {
      offset: 32
    }
  };
  const borderSubtle = hslToHex(Object.assign({}, surfaceHsl, {
    l: Math.max(surfaceHsl.l - BORDER_LIGHTNESS_CONFIG.subtle.offset, 0),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation)
  }));
  const borderDefault = hslToHex(Object.assign({}, surfaceHsl, {
    l: Math.max(surfaceHsl.l - BORDER_LIGHTNESS_CONFIG.default.offset, 0),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation)
  }));
  const borderHover = hslToHex(Object.assign({}, surfaceHsl, {
    l: Math.max(surfaceHsl.l - BORDER_LIGHTNESS_CONFIG.hover.offset, 0),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation)
  }));
  const RAISED_LIGHTNESS_OFFSET = 3;
  const raisedSurface = hslToHex(Object.assign({}, surfaceHsl, {
    l: Math.max(surfaceHsl.l - RAISED_LIGHTNESS_OFFSET, 0)
  }));
  return {
    // Portals uses subtler/lighter borders than Trellis light
    'border-core-default': borderDefault,
    'border-core-subtle': borderSubtle,
    'border-core-disabled': '#d1d1d1',
    // darker than Trellis #E6E6E6 for better visibility on light surfaces
    'border-core-hover': borderHover,
    'border-transitional-core-subtle': borderDefault,
    // Slightly gray raised surface (Trellis uses pure white)
    'fill-surface-raised': raisedSurface,
    // Gray field backgrounds (Trellis uses white #FFFFFF)
    'fill-field-default': surfaceVariants.subtle,
    'fill-field-default-alt': surfaceVariants.hover,
    // trellis-light's icon-inverse-default (#141414) is correct for dark icons on light surfaces,
    // but deriveTertiaryColorVariants sets it to getBestTextColor(accentColor) which is white for
    // dark accent colors — invisible on a white modal body. Override to near-black so the Modal
    // close icon is always visible in light mode regardless of the brand's accent color.
    'icon-inverse-default': '#141414'
  };
}
export function deriveTextInteractiveDefaultColorVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  return {
    'text-interactive-default': variants.default,
    'text-interactive-default-hover': variants.hover,
    'text-interactive-default-pressed': variants.pressed,
    'specialty-text-interactive-alt-default': variants.default,
    'specialty-text-interactive-alt-hover': variants.hover,
    'specialty-text-interactive-alt-pressed': variants.pressed,
    'border-interactive-default': variants.default,
    'specialty-text-core-alt-default': variants.default
  };
}
export function deriveColorFillCautionDefaultVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  const alertSubtle = deriveAlertSubtleColor(baseColor);
  return {
    // status tags
    'fill-caution-default': variants.default,
    'fill-caution-hover': variants.hover,
    'fill-caution-pressed': variants.pressed,
    'fill-accent-yellow-subtle-alt': alertSubtle,
    'fill-caution-subtle': alertSubtle,
    // tag components
    'border-caution-default': alertSubtle
  };
}
export function deriveColorFillAccentGreenDefaultVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  const alertSubtle = deriveAlertSubtleColor(baseColor);
  return {
    // status tags
    'fill-accent-green-default': variants.default,
    'fill-accent-green-subtle': variants.subtle,
    'fill-accent-green-subtle-alt': alertSubtle,
    'fill-accent-green-pressed': variants.pressed,
    'fill-accent-green-hover': variants.hover,
    // tag components
    'border-accent-green-default': alertSubtle,
    'border-positive-default': alertSubtle,
    'fill-positive-subtle': alertSubtle
  };
}
export function deriveColorFillAccentRedDefaultVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  const alertSubtle = deriveAlertSubtleColor(baseColor);
  const textColor = getBestTextColor(baseColor);
  const subtleTextColor = getBestTextColor(alertSubtle);
  return {
    // status tags
    'fill-accent-red-default': variants.default,
    'fill-accent-red-subtle': variants.subtle,
    'fill-accent-red-subtle-alt': alertSubtle,
    'fill-accent-red-pressed': variants.pressed,
    'fill-accent-red-hover': variants.hover,
    'fill-alert-subtle': alertSubtle,
    'fill-transitional-alert': variants.default,
    'border-alert-default': alertSubtle,
    // text
    'text-alert-default': subtleTextColor,
    'text-alert-on-fill-default': textColor
  };
}
export function deriveColorFillInfoDefaultVariants(baseColor) {
  const variants = deriveColorVariants(baseColor);
  const alertSubtle = deriveAlertSubtleColor(baseColor);
  return {
    'fill-info-default': variants.default,
    'fill-info-subtle': alertSubtle,
    'fill-info-hover': variants.hover,
    'fill-info-pressed': variants.pressed,
    'border-info-default': alertSubtle,
    'border-transitional-interactive-subtle': variants.default,
    'fill-accent-blue-default': variants.default,
    'fill-accent-blue-subtle': variants.subtle
  };
}
const tokenConfigMap = new Map();
for (const config of TOKEN_VARIANT_CONFIGS) {
  for (const prefix of config.prefixes) {
    tokenConfigMap.set(prefix, {
      variants: config.variants,
      derivedTokens: config.derivedTokens
    });
  }
}
function getConfigForTokenPrefix(tokenPrefix) {
  const exactMatch = tokenConfigMap.get(tokenPrefix);
  if (exactMatch) {
    return exactMatch;
  }
  return {
    variants: SINGLE_VARIANT
  };
}

// Derives all variant tokens for a given base color and target token.
export function deriveGenericColorVariants(baseColor, targetToken) {
  var _config$derivedTokens, _config$derivedTokens2, _config$derivedTokens3;
  const tokenPrefix = targetToken.replace(/-(default|subtle|hover|pressed)$/, '');
  const config = getConfigForTokenPrefix(tokenPrefix);
  if (config.variants.length === 0) {
    return {
      [targetToken]: baseColor
    };
  }
  const colorVariants = deriveColorVariants(baseColor);
  const result = {};
  for (const variant of config.variants) {
    const variantKey = variant;
    if (colorVariants[variantKey]) {
      result[`${tokenPrefix}-${variant}`] = colorVariants[variantKey];
    }
  }
  if ((_config$derivedTokens = config.derivedTokens) !== null && _config$derivedTokens !== void 0 && _config$derivedTokens.text) {
    const textColor = getBestTextColor(baseColor);
    result[`${config.derivedTokens.text}-default`] = textColor;
  }
  if ((_config$derivedTokens2 = config.derivedTokens) !== null && _config$derivedTokens2 !== void 0 && _config$derivedTokens2.border) {
    result[`${config.derivedTokens.border}-default`] = baseColor;
  }
  if ((_config$derivedTokens3 = config.derivedTokens) !== null && _config$derivedTokens3 !== void 0 && _config$derivedTokens3.icon) {
    const iconColor = getBestTextColor(baseColor);
    result[`${config.derivedTokens.icon}-default`] = iconColor;
  }
  return result;
}
export const COLOR_OVERRIDE_CONFIGS = {
  'fill-primary-default': {
    brandField: 'primaryColor',
    themeSettingField: 'trellisColorFillPrimaryDefault',
    deriveVariants: derivePrimaryColorVariants
  },
  'fill-secondary-default': {
    brandField: 'secondaryColor',
    themeSettingField: 'trellisColorFillSecondaryDefault',
    deriveVariants: deriveSecondaryColorVariants
  },
  'fill-tertiary-default': {
    brandField: 'accentColor1',
    themeSettingField: 'trellisColorFillTertiaryDefault',
    deriveVariants: deriveTertiaryColorVariants
  },
  'text-interactive-default': {
    brandField: null,
    themeSettingField: 'trellisColorTextInteractiveDefault',
    deriveVariants: deriveTextInteractiveDefaultColorVariants
  },
  // The below configs are not customizable via CMS theme fields
  // but necessary to allow us to derive the necessary variants for
  // the default token values we set for the base color token keys
  // (e.g. fill-caution-default, fill-accent-green-default, etc)
  'fill-caution-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: deriveColorFillCautionDefaultVariants
  },
  'fill-accent-green-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: deriveColorFillAccentGreenDefaultVariants
  },
  'fill-accent-red-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: deriveColorFillAccentRedDefaultVariants
  },
  'fill-info-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: deriveColorFillInfoDefaultVariants
  },
  // Accent colors used by Badge (free, beta, count, dot variants).
  // Uses deriveGenericColorVariants which produces fill variants (default, hover, pressed, subtle)
  // based on TOKEN_VARIANT_CONFIGS (COMPLETE_VARIANT).
  'fill-accent-orange-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: color => deriveGenericColorVariants(color, 'fill-accent-orange-default')
  },
  'fill-accent-purple-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: color => deriveGenericColorVariants(color, 'fill-accent-purple-default')
  }
};
export const SURFACE_STYLE_OVERRIDE_CONFIG = {
  themeSettingField: 'trellisColorFillSurfaceDefault',
  deriveVariants: deriveSurfaceTokensFromStyle
};

// Borders
export function deriveBorderRadiusVariants(borderRadiusChoice) {
  // This is the default so we will fallback to the current borderRadius values
  // within Trelils.
  if (borderRadiusChoice === 'rounded') {
    return {
      '0': '0px',
      '100': '4px',
      '200': '4px',
      '300': '8px',
      '400': '16px',
      full: '999999px',
      'transitional-container': '8px',
      'transitional-container-alt': '8px',
      'transitional-component': '4px',
      'transitional-floating': '16px',
      'transitional-full-0': '999999px',
      'transitional-full-3': '999999px',
      'transitional-full-8': '999999px'
    };
  } else if (borderRadiusChoice === 'pill') {
    return {
      '0': '16px',
      '100': '999999px',
      '200': '999999px',
      '300': '999999px',
      '400': '999999px',
      full: '999999px',
      'transitional-container': '16px',
      'transitional-container-alt': '16px',
      'transitional-component': '8px',
      'transitional-floating': '16px',
      'transitional-full-0': '999999px',
      'transitional-full-3': '999999px',
      'transitional-full-8': '999999px'
    };
  } else {
    return {
      '0': '0px',
      '100': '0px',
      '200': '0px',
      '300': '0px',
      '400': '0px',
      full: '999999px',
      'transitional-container': '0px',
      'transitional-container-alt': '0px',
      'transitional-component': '0px',
      'transitional-floating': '0px',
      'transitional-full-0': '0px',
      'transitional-full-3': '0px',
      'transitional-full-8': '0px'
    };
  }
}
export const BORDER_RADIUS_OVERRIDE_CONFIGS = [{
  themeSettingField: 'trellisBorderRadius',
  deriveVariants: deriveBorderRadiusVariants
}];
function buildFontFamilyString(fontName) {
  return `${fontName}, sans-serif`;
}

// Fonts
const BODY_TYPOGRAPHY_TOKENS = ['body-100', 'body-125', 'body-200', 'body-300', 'body-400', 'body-500', 'body-600', 'body-700', 'detail-100', 'detail-200', 'link-25', 'link-100', 'link-200'];
const HEADING_TYPOGRAPHY_TOKENS = ['heading-25', 'heading-50', 'heading-100', 'heading-200', 'heading-300', 'heading-400', 'heading-500', 'heading-600', 'heading-700', 'heading-800', 'heading-900', 'heading-1000', 'display-100', 'display-200', 'display-300', 'display-400', 'display-500'];
function deriveFontVariants(fontFamily, tokens) {
  const typographyOverrides = {};
  const fullFontFamily = buildFontFamilyString(fontFamily);
  tokens.forEach(token => {
    typographyOverrides[token] = {
      fontFamily: fullFontFamily
    };
  });
  return typographyOverrides;
}
export function deriveBodyFontVariants(fontFamily) {
  return deriveFontVariants(fontFamily, BODY_TYPOGRAPHY_TOKENS);
}
export function deriveHeadingFontVariants(fontFamily) {
  return deriveFontVariants(fontFamily, HEADING_TYPOGRAPHY_TOKENS);
}
export const FONT_OVERRIDE_CONFIGS = {
  body: {
    brandField: 'bodyFont',
    themeSettingField: 'bodyFont',
    deriveVariants: deriveBodyFontVariants
  },
  heading: {
    brandField: 'primaryFont',
    themeSettingField: 'primaryFont',
    deriveVariants: deriveHeadingFontVariants
  }
};